import { TypedDocumentNode, gql } from '@apollo/client';
import QueryString from 'qs';
import { CSSProperties } from 'react';
import { FormattedMessage } from 'react-intl';
import { generatePath, useSearchParams } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { Link } from '@sorare/routing';

import { Rarity, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Market } from '@sorare/core/src/atoms/icons/Market';
import { StarBall } from '@sorare/core/src/atoms/icons/StarBall';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { DisplayL, LabelL, LabelM } from '@sorare/core/src/atoms/typography';
import { Dialog } from '@sorare/core/src/components/dialog';
import {
  FOOTBALL_CRAFT_RARITY,
  FOOTBALL_MARKET_COMMON_PACKS,
} from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { scarcityMessages } from '@sorare/core/src/lib/scarcity';
import { toCamelCase } from '@sorare/core/src/lib/toCamelCase';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';
import { theme } from '@sorare/core/src/style/theme';

import { cellBackground } from '../styles';
import { EssenceOpeningOnboardingDialog_probabilisticBundleSlotCardShardItem } from './__generated__/index.graphql';

const StyledDialog = styled(Dialog)`
  --height: var(--100vh);
  @media ${tabletAndAbove} {
    --height: min(100vh - 8 * var(--unit), 812px);
  }
  height: var(--height);
  & > * {
    overflow: hidden;
    background: transparent;
  }
`;

const InnerWrapper = styled(Vertical)`
  ${cellBackground}
  height: 100%;
`;
const ImgWrapper = styled(Vertical)`
  flex: 1;
`;

const YouGainedLabel = styled(LabelM)`
  background: linear-gradient(329.81deg, #048a3d 2.3%, #bff6d6 95.33%);
  background-size: 100%;
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  -moz-text-fill-color: transparent;
  align-self: flex-start;
`;

const Content = styled(Vertical).attrs({ gap: 4 })`
  padding: var(--quadruple-unit) var(--double-unit);
  bottom: var(--double-unit);
  @media ${tabletAndAbove} {
    position: static;
    justify-content: flex-end;
  }
`;
const StyledStarBall = styled(StarBall)`
  margin-top: -40px;
`;

const Badge = styled(Horizontal).attrs({ gap: 0, center: true })`
  border-radius: var(--double-unit);
  width: min-content;
  padding: 2px var(--unit);
  border: 1px solid #ffb800;
  background: linear-gradient(-25deg, #ffb8001a 50%, transparent 50%)
    padding-box;
  align-self: center;
  margin-top: -50px;
`;

const gradientText = css`
  background: var(--gradient-background);
  background-size: 100%;
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  -moz-text-fill-color: transparent;
`;

const BadgeText = styled(LabelL)`
  ${gradientText}
`;

const Title = styled(DisplayL)`
  ${gradientText}
`;

const Img = styled.img`
  filter: drop-shadow(0px 0px 70px var(--drop-shadow-color));
`;

const InlineIconWrapper = styled.span`
  & > * {
    margin-bottom: 3px;
  }
`;

type Props = {
  open: boolean;
  onClose: () => void;
  shardSlotItem: EssenceOpeningOnboardingDialog_probabilisticBundleSlotCardShardItem;
  disableOnClose?: boolean;
};

// TODO: get this from backend
export const LIMITED_TIER_5_THRESHOLD = 20;

const Description = ({
  shardSlotItem,
}: {
  shardSlotItem: EssenceOpeningOnboardingDialog_probabilisticBundleSlotCardShardItem;
}) => {
  const { sport } = useSportContext();
  if (shardSlotItem.rarity === Rarity.common) {
    if (sport !== Sport.FOOTBALL) {
      return null;
    }
    return (
      // assume that user has enough Essence to buy a common pack (Common Game onboarding behavior)
      <FormattedMessage
        id="CraftCardDialog.description"
        defaultMessage="You accumulated enough Essence to purchase a pack in the Market ({icon})."
        values={{
          icon: (
            <InlineIconWrapper as={Link} to={FOOTBALL_MARKET_COMMON_PACKS}>
              <Market size={2} />
            </InlineIconWrapper>
          ),
        }}
      />
    );
  }
  const canCraft = shardSlotItem.quantity >= LIMITED_TIER_5_THRESHOLD;
  if (canCraft) {
    return (
      <FormattedMessage
        id="CraftCardDialog.description2"
        defaultMessage="You collected enough Essence to craft a powerful Limited Card in the Card Factory"
      />
    );
  }
  return (
    <FormattedMessage
      id="EssenceOpeningOnboardingDialog.description"
      defaultMessage="Accumulate enough Essence to craft powerful Limited Cards in the Card Factory."
    />
  );
};

const Cta = ({
  shardSlotItem,
  onClose,
}: {
  shardSlotItem: EssenceOpeningOnboardingDialog_probabilisticBundleSlotCardShardItem;
  onClose: () => void;
}) => {
  const canCraft =
    shardSlotItem.quantity >= LIMITED_TIER_5_THRESHOLD &&
    shardSlotItem.rarity !== Rarity.common;
  const [searchParams] = useSearchParams();

  if (canCraft) {
    return (
      <Button
        size="large"
        color="secondary"
        to={`${generatePath(FOOTBALL_CRAFT_RARITY, { rarity: 'limited' })}${QueryString.stringify(
          { redirectUrl: searchParams.get('redirectUrl') },
          {
            addQueryPrefix: true,
          }
        )}`}
      >
        <FormattedMessage
          id="CraftCardDialog.craftCardCta"
          defaultMessage="Craft My Card"
        />
      </Button>
    );
  }
  return (
    <Button size="large" color="secondary" onClick={onClose}>
      <FormattedMessage
        id="EssenceOpeningOnboardingDialog.cta"
        defaultMessage="Got it"
      />
    </Button>
  );
};

export const EssenceOpeningOnboardingDialog = ({
  open,
  onClose,
  shardSlotItem,
  disableOnClose,
}: Props) => {
  return (
    <StyledDialog
      open={open}
      onClose={disableOnClose ? undefined : onClose}
      maxWidth="xs"
    >
      <InnerWrapper
        style={
          {
            '--gradient-background':
              theme.colors.scarcity[toCamelCase(shardSlotItem.rarity)],
          } as CSSProperties
        }
      >
        <ImgWrapper center>
          <Img
            src={`https://frontend-assets.sorare.com/football/shards/${shardSlotItem.rarity}.png`}
            alt=""
            width={160}
            style={
              {
                '--drop-shadow-color':
                  shardSlotItem.rarity === Rarity.common
                    ? '#ffffff'
                    : '#ffc700',
              } as CSSProperties
            }
          />
          <Badge
            style={{
              borderColor:
                shardSlotItem.rarity === Rarity.common ? '#d1dcdf' : '#ae5e13',
            }}
          >
            <BadgeText bold>{`x${shardSlotItem.quantity}`}</BadgeText>
          </Badge>
        </ImgWrapper>

        <Content gap={4}>
          <Vertical gap={1}>
            <YouGainedLabel>
              <FormattedMessage
                id="EssenceOpeningOnboardingDialog.subtitle"
                defaultMessage="You won"
              />
            </YouGainedLabel>
            <Title>
              <FormattedMessage
                id="EssenceOpeningOnboardingDialog.title2"
                defaultMessage="{rarity}<br></br>Essence"
                values={{
                  br: () => <br />,
                  rarity: (
                    <FormattedMessage
                      {...scarcityMessages[shardSlotItem.rarity]}
                    />
                  ),
                }}
              />
              <StyledStarBall
                width={16}
                color={
                  <linearGradient id="starball" x1="1" y1="1" x2="0" y2="0">
                    <stop
                      offset="0%"
                      stopColor={
                        shardSlotItem.rarity === Rarity.common
                          ? '#d1dcdf'
                          : '#ae5e13'
                      }
                    />
                    <stop
                      offset="100%"
                      stopColor={
                        shardSlotItem.rarity === Rarity.common
                          ? '#ffffff'
                          : '#dfad46'
                      }
                    />
                  </linearGradient>
                }
              />
            </Title>
            <LabelM>
              <Description shardSlotItem={shardSlotItem} />
            </LabelM>
          </Vertical>
          <Cta shardSlotItem={shardSlotItem} onClose={onClose} />
        </Content>
      </InnerWrapper>
    </StyledDialog>
  );
};

EssenceOpeningOnboardingDialog.fragments = {
  shardSlotItem: gql`
    fragment EssenceOpeningOnboardingDialog_probabilisticBundleSlotCardShardItem on ProbabilisticBundleSlotCardShardItem {
      id
      quantity
      rarity
    }
  ` as TypedDocumentNode<EssenceOpeningOnboardingDialog_probabilisticBundleSlotCardShardItem>,
};
